import React, { useState } from "react";
import { DKVColorBoxLabel, KVColorBoxLabel } from "../../components/ColorBox";
import { MoreToggle } from "../../components/MoreToggle";
import { useSelected, useSelectionFor } from "../../state";
import { CodeDataDTO, Cube } from "../../state/types";
import { footprintRows } from "./footprints";
import { Omgeving } from "./Omgeving";
import { Ownership, type OwnershipProps } from "./Ownership";
import { Publications } from "./Publications";
import { Section } from "./Section";
import { A, capitalize, Info, KV, rFormat, SubHeader } from "./util";

const SoilPolicyAuthority = ({ data }) =>
  !data?.sites?.length ? null : (
    <>
      Alternatief:{" "}
      {data.sites.map(({ url, description }) => (
        <A key={url} href={url}>
          {String(description || "link")}
        </A>
      ))}
    </>
  );

const BodemLoketSoilPolicyAuthority = ({ data }) => (
  <>
    (<A href="https://www.bodemloket.nl/kaart">bodemloket</A>).
    <SoilPolicyAuthority data={data} />
  </>
);

const SoilExcavation = ({ data }) => data?.value || null;

const fixUrl = (url: string) => {
  if (!url.includes("://")) {
    return "https://" + url;
  }
  return url;
};

const SoilPolicy = ({ data }) => (
  <>
    {data.internet && <A href={fixUrl(data.internet)}>Informatie</A>}{" "}
    {data.internetapplicatie && <A href={fixUrl(data.internetapplicatie)}>Applicatie</A>}{" "}
  </>
);

const WBB = ({ field }: { field: Cube }) => {
  const { soilPolicyAuthority, soilPolicy, soilExcavation, soilApplication } = field?.data || {};
  if (!soilPolicyAuthority) return null;
  const SPComponent =
    soilPolicyAuthority?.[0].extId === "3" ? BodemLoketSoilPolicyAuthority : SoilPolicyAuthority;
  return (
    <>
      <SubHeader>Bodembeleid</SubHeader>
      <Info as="dl">
        <DKVColorBoxLabel
          k="Bevoegd gezag"
          elem={soilPolicyAuthority}
          DataComponent={SPComponent}
        />
        <DKVColorBoxLabel k="Bodembeleid" elem={soilPolicy} DataComponent={SoilPolicy} />
        <DKVColorBoxLabel k="Ontgraving" elem={soilExcavation} DataComponent={SoilExcavation} />
        <DKVColorBoxLabel k="Toepassing" elem={soilApplication} />
      </Info>
    </>
  );
};

const description = (
  <>
    Hier inventariseren we de juridische informatie. Denk aan beperkingen, bodemrichtlijn,
    bodemkwaliteit, bodembescherming.
  </>
);

// Het bouwvlak uit het bestemmingsplan. Klikken licht het vlak op de kaart op
// (zelfde selectie-store als een gebouw in de tab Gebouwen); de laag zelf is
// zichtbaar zolang deze sectie actief is, zie FootprintsControl.
const Footprints = ({ cube }: { cube: Cube }) => {
  const rows = footprintRows(cube);
  const selected = useSelectionFor<number>("footprint");
  const toggle = useSelected((s) => s.toggle);
  if (!rows.length) return null;
  return (
    <KV k={rows.length === 1 ? "Bouwvlak" : "Bouwvlakken"}>
      {rows.map((row) => (
        <button
          key={row.index}
          type="button"
          onClick={() => toggle("footprint", row.index)}
          title="Toon op de kaart"
          className={`block w-full text-left rounded px-1 -mx-1 cursor-pointer hover:bg-bright-yellow/40 ${
            selected === row.index ? "bg-bright-yellow/60" : ""
          }`}
        >
          {row.overlap}% van het perceel, {rFormat.format(row.areaM2)}&nbsp;m²
          {rows.length > 1 && (
            <span className="text-gray-500">
              {" "}
              ({row.label} {row.index + 1})
            </span>
          )}
        </button>
      ))}
    </KV>
  );
};

const Policy = ({ e }) => {
  return (
    <>
      {e.name} {e.data?.idesc && <A href={e.data.url}>{e.data.idesc}</A>}
      <br />
    </>
  );
};

const PBB = ({ e }) => (
  <>
    {e.name}, <span className="bg-yellow/10 font-mono">{e.data?.gebaseerd_op_stuk}</span>
    <br />
  </>
);

interface ShortenI {
  k: string;
  data: any[];
  shorten: (e: any) => string;
  Component: React.FC<any>;
}
const Shorten = ({ k, data, shorten, Component }: ShortenI) => {
  const [open, setOpen] = useState(false);
  if (!data) return null;
  let content;
  if (!open && data.length > 2) {
    const names = data.map(shorten).filter((e) => e);
    const unique = names.reduce((acc, name) => {
      if (!acc.includes(name)) acc.push(name);
      return acc;
    }, []);
    content = unique.join(", ");
  } else {
    // Kaartlaagitems zonder gegarandeerd unieke extId, in vaste volgorde
    // eslint-disable-next-line @eslint-react/no-array-index-key
    content = data?.map((e, index) => <Component key={index} e={e} />);
  }

  return (
    <KV k={k}>
      {content} {data.length > 2 && <MoreToggle open={open} setOpen={setOpen} />}
    </KV>
  );
};

const VillageCity = ({ data }) => {
  if (!data) return null;
  const type = data.data.type;
  const key = type.includes("stad") ? "stads" : "dorps";
  return (
    <KV k={`Beschermd ${key}gezicht`}>
      {data.name} <A href={data.data.link}>{type}</A>
    </KV>
  );
};

const LIBInfo = ({ data }) => (
  <i>
    <br />
    Categorie {data.name} <A href="https://wetten.overheid.nl/BWBR0014329/2025-07-01">LIB</A>
  </i>
);
const LIB = ({ data }) => {
  if (!data) return null;
  const strictest = data.reduce((m, e) => (e.extId < m.extId ? e : m), { extId: "9" });
  return (
    <KVColorBoxLabel k="Luchthaven­indelings­besluit" elem={strictest} DataComponent={LIBInfo} />
  );
};

const REV = ({ data }: { data: CodeDataDTO[] }) => {
  if (!data) return null;
  const names = [
    ...new Set(data?.map((e) => e.name + (e.data.company ? ` (${e.data.company})` : ""))),
  ];

  return (
    <KV k="Register Externe Veiligheid">
      {/* Uit een Set, dus uniek */}
      {names.map((name) => (
        <React.Fragment key={name}>
          {name.replace("_", " ")}
          <br />
        </React.Fragment>
      ))}
    </KV>
  );
};

interface PropsWithLength {
  length: number;
}
const Access = ({ field }: { field: Cube }) => {
  const [open, setOpen] = useState(false);
  const { perimeter } = field.geometry.properties;
  const byName: Record<string, PropsWithLength> = field.access.reduce((memo, a) => {
    const e = memo[a.name];
    memo[a.name] = { ...e, ...a, length: (e?.length || 0) + a.length };
    return memo;
  }, {});
  const total = Object.values(byName).reduce((acc, e) => acc + e.length, 0);
  const streets = Object.keys(byName).sort((a, b) => byName[b].length - byName[a].length);
  let verdict;
  if (total < 0.05) verdict = "Niet ontsloten";
  else if (total < 2) verdict = "Kort ontsloten";
  else if (total / perimeter < 0.05) verdict = "Minimaal Ontsloten";
  else verdict = "Goed ontsloten" + (streets.length > 1 ? ", meerdere opties" : "");
  return (
    <KV k="Ontsluiting">
      {verdict}{" "}
      {streets.length > 0 && (
        <>
          (<i>{streets.slice(0, open ? undefined : 2).join(", ")}</i>)
        </>
      )}{" "}
      {streets.length > 2 && <MoreToggle open={open} setOpen={setOpen} />}
    </KV>
  );
};

export const SectionBuild = ({ field, ownership }: { field: Cube; ownership?: OwnershipProps }) => {
  const { zoningDouble, zoningSingle, limit, imnaPolicy, nllib, rev } = field.data;
  const { silence, corp, state, builtUpBuffer, urbanAreaBuffer } = field.data;
  const { nationalPark, novex, industry, builtUpArea, villageview, urbanArea } =
    field.administrative;
  const { Grondeigendom } = field.rast;
  return (
    <Section name="build" description={description}>
      <Info as="dl">
        <KV k="Bestemming" check={!!(zoningSingle || zoningDouble)}>
          {[...new Set(zoningSingle?.map((s) => s.name))].join(", ")}
          {zoningSingle && zoningDouble && ", "}
          {zoningDouble && "dubbelbestemming: "}
          {[...new Set(zoningDouble?.map((s) => s.data.naam || s.name))].join(", ")}
        </KV>
        {zoningSingle && (
          <KV k={capitalize(zoningSingle[0].data.type)}>
            <A href={zoningSingle[0].data.link}>{capitalize(zoningSingle[0].data.status)}</A>
          </KV>
        )}
        <Footprints cube={field} />
        <KV k="Bebouwde kom" check={!!builtUpArea}>
          {builtUpArea?.name}, {builtUpArea?.data?.typegebied}
        </KV>
        <KV k="Bebouwde kom" check={!!builtUpBuffer}>
          &lt;{builtUpBuffer?.[0]?.name} van bebouwde kom
        </KV>
        <KV k="Stedelijk gebied" check={!!urbanArea}>
          {urbanArea?.name || "Binnen stedelijk gebied"}
        </KV>
        <KV k="Stedelijk gebied" check={!!urbanAreaBuffer}>
          &lt;{urbanAreaBuffer?.[0]?.name} van stedelijk gebied
        </KV>
        <REV data={rev} />
        <LIB data={nllib} />
        <VillageCity data={villageview} />
        <KV k="Nationaal park" check={!!nationalPark}>
          {nationalPark?.name} {JSON.stringify(nationalPark?.data)}
        </KV>
        <KV k="Novex gebied" check={!!novex}>
          {novex?.name}
        </KV>
        <KV k="Industrie terrein" check={!!industry}>
          {industry?.name}
        </KV>
        <KV k="Eigendom" check={!!(Grondeigendom || state || corp)}>
          {Grondeigendom?.name} {state?.[0]?.name} {corp && `Corporatie ${corp?.[0]?.name}`}
        </KV>
        <KV k="Stilte" check={!!silence}>
          Stiltegebied
        </KV>
        <Shorten
          k="Publiekrechtelijke beperkingen"
          data={limit}
          shorten={(e) => e.name.match(/\w*wet/)?.[0]}
          Component={PBB}
        />
        <Shorten
          k="Beleid"
          data={imnaPolicy}
          shorten={(e) => e.data?.idesc || e.name}
          Component={Policy}
        />
        <Access field={field} />
      </Info>
      {/* Direct onder het cube-veld "Eigendom": wat het Kadaster erover levert */}
      {ownership && <Ownership {...ownership} />}

      {/* <KV k={"Bestemmingsplan"}>Buitengebied Tiel</KV>
      <KV k={"Structuurvisie"}>Structuurvisie Gemeente Tiel</KV> */}
      <WBB field={field} />
      <Publications cube={field} />
      <Omgeving cube={field} />

      {/* <SubHeader title={title}>Wet bodembescherming</SubHeader>
    <Info as="dl">
      <KV k="Categorie">Gegevens aanwezig, status onbekend</KV>
      <KV k="Vervolg">Null</KV>
      <KV k="Status oordeel">Onbekend</KV>
      <KV k="Administrator">Omgevingsdienst Haaglanden</KV>
      <KV k="BIS locatie">AA184206427</KV>
      <KV k="SIKB-ID">Onbekend</KV>
      <KV k="WBB Dossier nummer">1222123123</KV>
    </Info>
    <Legend>
      <LegendItem color="#00FF00" name="Saneringsactiviteit" />
      <LegendItem color="#40FF23" name="Voldoende onderzocht/gesaneerd" />
      <LegendItem color="#AAFF00" name="Onderzoek uitvoeren" />
      <LegendItem color="#0000FF" name="Onbekend" />
    </Legend>
    <BigButton>Rapport downloaden</BigButton>
    <SubHeader title={title}>Bodembeleidskaart</SubHeader>
    <Legend>
      <LegendItem color="#00FF00" name="Generiek beleid" />
      <LegendItem color="#40FF23" name="Gebiedspecifiek beleid (nota)" />
      <LegendItem color="#AAFF00" name="Generiek beleid met PFAS" />
      <LegendItem color="#0000FF" name="Gebiedspecifiek beleid met PFAS" />
    </Legend> */}
      {/* https://www.bodemrichtlijn.nl/Bibliotheek/grondstromen/gebruiken-bodemkwaliteitskaart */}
      {/* <SubHeader title={title}>Ontgravings- of toepassingskaart bovengrond</SubHeader>
    <Legend>
      <LegendItem color="#00FF00" name="Industrie" />
      <LegendItem color="#40FF23" name="Lokale maximale waarde" />
      <LegendItem color="#AAFF00" name="Landbouw / Natuur" />
      <LegendItem color="#0000FF" name="Wonen" />
      <LegendItem color="#1234AA" name="Overig" />
    </Legend> */}
    </Section>
  );
};
